import React from 'react'
import Dialog from '@mui/material/Dialog'
import Typography from '@mui/material/Typography'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faArchive, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './AutoSubmitConfirmDialog.styles'

interface FundingConfirmDialogProps {
  open: boolean
  title: string
  body: React.ReactNode
  confirmLabel: string
  confirmIcon?: IconDefinition
  onCancel: () => void
  onConfirm: () => void
  confirmTestId?: string
  /** Defaults to archive icon (Archive / Unarchive flows). */
  icon?: IconDefinition
}

/** Shared Cancel / primary-confirm modal (Archive, status, etc.). */
export const FundingConfirmDialog = ({
  open,
  title,
  body,
  confirmLabel,
  confirmIcon,
  onCancel,
  onConfirm,
  confirmTestId = 'funding-confirm-proceed',
  icon = faArchive,
}: FundingConfirmDialogProps) => {
  const { classes, cx } = useStyles()

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      classes={{ paper: classes.paper }}
      data-testid="funding-confirm-dialog"
    >
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.titleGroup}>
            <span className={classes.titleIcon}>
              <FontAwesomeIcon icon={icon} />
            </span>
            <Typography component="h2" className={classes.title}>
              {title}
            </Typography>
          </div>
        </div>
        <button
          type="button"
          className={classes.close}
          aria-label="Close"
          onClick={onCancel}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <div className={classes.body}>
        {typeof body === 'string' ? (
          <Typography component="p" className={classes.bodyText}>
            {body}
          </Typography>
        ) : (
          body
        )}
      </div>

      <div className={classes.footer}>
        <button
          type="button"
          className={cx(classes.button, classes.cancelButton)}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className={cx(classes.button, classes.proceedButton, {
            [classes.proceedButtonWithIcon]: Boolean(confirmIcon),
          })}
          onClick={onConfirm}
          data-testid={confirmTestId}
        >
          {confirmLabel}
          {confirmIcon ? <FontAwesomeIcon icon={confirmIcon} /> : null}
        </button>
      </div>
    </Dialog>
  )
}
